import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Shield,
  RefreshCw,
  Loader2,
  XCircle,
  Plus,
  Timer,
  Flame,
  CheckCircle2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { demosAPI, leadsAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';
import { toast } from '../../hooks/use-toast';

const getStatusColor = (status) => {
  const colors = {
    SCHEDULED: 'bg-blue-100 text-blue-700 border-blue-200',
    RESCHEDULED: 'bg-amber-100 text-amber-700 border-amber-200',
    COMPLETED_QUALIFIED_SQD: 'bg-emerald-100 text-emerald-700 border-emerald-200',

    COMPLETED_NOT_QUALIFIED: 'bg-orange-100 text-orange-700 border-orange-200',
    CANCELLED_BY_CLIENT: 'bg-red-100 text-red-700 border-red-200',
    NO_SHOW: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
};

const DemosPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { confirm, dialogProps } = useConfirm();

  const [demos, setDemos] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modal states
  const [isCompleteDemoOpen, setIsCompleteDemoOpen] = useState(false);
  const [isRescheduleDemoOpen, setIsRescheduleDemoOpen] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState(null);
  
  const [completeDemoForm, setCompleteDemoForm] = useState({
    demo_done_by: [''],
    demo_rating: 5,
    demo_actual_start: '',
    demo_actual_end: '',
    demo_notes: ''
  });
  
  const [rescheduleForm, setRescheduleForm] = useState({
    demo_date: '',
    demo_time: '',
    reschedule_reason: '',
    remarks: ''
  });
  
  const isAdminOrManager = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [demosRes, leadsRes] = await Promise.all([
          demosAPI.list({ page_size: 100 }),
          leadsAPI.list({ page_size: 100 })
        ]);
        
        const demosData = Array.isArray(demosRes.data) ? demosRes.data : demosRes.data?.demos || [];
        const leadsData = Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data?.leads || [];
        
        setDemos(demosData);
        setLeads(leadsData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const refreshData = async () => {
    try {
      const demosRes = await demosAPI.list({ page_size: 500 });
      const demosData = Array.isArray(demosRes.data) ? demosRes.data : demosRes.data?.demos || [];
      setDemos(demosData);
    } catch (e) {
      console.error('Refresh failed:', e);
    }
  };

  // Filter demos
  const activeDemos = useMemo(() => {
    return demos.filter(d => 
        ['SCHEDULED', 'RESCHEDULED', 'COMPLETED_QUALIFIED_SQD', 'COMPLETED_NOT_QUALIFIED'].includes(d.status)
    );
  }, [demos]);

  // Demos awaiting qualification
  const pendingQualificationDemos = useMemo(() => {
    return demos.filter(d => d.status === 'COMPLETED_NOT_QUALIFIED');
  }, [demos]);

  const getLeadInfo = (leadId) => {
    return leads.find(l => l.id === leadId) || {};
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    const startPadding = firstDay.getDay();
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getDemosForDate = (date) => {
    if (!date) return [];
    
    // Use local date parts for comparison to match calendar cell which is rendered in local time
    const targetDateStr = date.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
    
    return activeDemos.filter(demo => {
      // demo.demo_datetime is UTC ISO string
      const demoDate = new Date(demo.demo_datetime);
      const demoDateStr = demoDate.toLocaleDateString('en-CA'); // Convert UTC to local YYYY-MM-DD
      return demoDateStr === targetDateStr;
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const calculateDemoDuration = () => {
    if (completeDemoForm.demo_actual_start && completeDemoForm.demo_actual_end) {
      const start = new Date(completeDemoForm.demo_actual_start);
      const end = new Date(completeDemoForm.demo_actual_end);
      const diffMs = end - start;
      const diffMins = Math.round(diffMs / 60000);
      return diffMins > 0 ? diffMins : 0;
    }
    return 0;
  };

  const handleCompleteDemo = async () => {
    if (!selectedDemo) return;
    if (completeDemoForm.demo_done_by.filter(n => n.trim()).length === 0) {
      toast({
        variant: 'destructive',
        title: 'Missing details',
        description: 'Please enter at least one person who conducted the demo.'
      });
      return;
    }
    try {
      await demosAPI.complete(selectedDemo.id, {
        demo_done_by: completeDemoForm.demo_done_by.filter(n => n.trim()),
        demo_rating: completeDemoForm.demo_rating,
        demo_actual_start: new Date(completeDemoForm.demo_actual_start).toISOString(),
        demo_actual_end: new Date(completeDemoForm.demo_actual_end).toISOString(),
        demo_notes: completeDemoForm.demo_notes
      });
      setIsCompleteDemoOpen(false);
      setCompleteDemoForm({ demo_done_by: [''], demo_rating: 5, demo_actual_start: '', demo_actual_end: '', demo_notes: '' });
      setSelectedDemo(null);
      await refreshData();
    } catch (error) {
      console.error('Failed to complete demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to complete demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleRescheduleDemo = async () => {
    if (!selectedDemo) return;
    try {
      const demoDateTime = new Date(`${rescheduleForm.demo_date}T${rescheduleForm.demo_time}`);
      await demosAPI.reschedule(selectedDemo.id, {
        demo_datetime: demoDateTime.toISOString(),
        reschedule_reason: rescheduleForm.reschedule_reason,
        remarks: rescheduleForm.remarks
      });
      setIsRescheduleDemoOpen(false);
      setRescheduleForm({ demo_date: '', demo_time: '', reschedule_reason: '', remarks: '' });
      setSelectedDemo(null);
      await refreshData();
    } catch (error) {
      console.error('Failed to reschedule demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to reschedule demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleCancelDemo = async (demoId) => {
    const ok = await confirm({
      title: 'Cancel this demo?',
      description: 'You can reschedule it later.',
      confirmText: 'Cancel Demo',
      cancelText: 'Keep Demo',
      destructive: true
    });
    if (!ok) return;
    try {
      await demosAPI.cancel(demoId);
      await refreshData();
    } catch (error) {
      console.error('Failed to cancel demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to cancel demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleQualifyDemo = async (demoId) => {
    try {
      await demosAPI.markSQD(demoId, 'Qualified by manager/admin');
      await refreshData();
    } catch (error) {
      console.error('Failed to qualify demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to mark SQD',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const addDemoPerson = () => setCompleteDemoForm(prev => ({ ...prev, demo_done_by: [...prev.demo_done_by, ''] }));
  const updateDemoPerson = (index, value) => {
    setCompleteDemoForm(prev => {
      const updated = [...prev.demo_done_by];
      updated[index] = value;
      return { ...prev, demo_done_by: updated };
    });
  };
  const removeDemoPerson = (index) => {
    if (completeDemoForm.demo_done_by.length > 1) {
      setCompleteDemoForm(prev => ({
        ...prev,
        demo_done_by: prev.demo_done_by.filter((_, i) => i !== index)
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(currentDate);
  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Demo Calendar</h1>
          <p className="text-slate-500 text-sm">View and manage scheduled demos</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-50">
            {activeDemos.filter(d => d.status === 'SCHEDULED').length} Scheduled
          </Badge>
          {isAdminOrManager && pendingQualificationDemos.length > 0 && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700">
              {pendingQualificationDemos.length} Pending Qualification
            </Badge>
          )}
        </div>
      </div>

      {/* Pending Qualification Section */}
      {isAdminOrManager && pendingQualificationDemos.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Demos Awaiting Qualification
            </CardTitle>
            <p className="text-sm text-orange-600">Click on company name to view lead details</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingQualificationDemos.map(demo => {
                const lead = getLeadInfo(demo.lead_id);
                const demoDate = new Date(demo.demo_datetime);
                const completedDate = demo.completed_at ? new Date(demo.completed_at) : null;
                
                return (
                  <div key={demo.id} className="p-4 bg-white rounded-lg border space-y-3">
                    {/* Header Row - Company Name (clickable) */}
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex-1 cursor-pointer hover:text-blue-600"
                        onClick={() => navigate(`/leads/${demo.lead_id}`)}
                      >
                        <p className="font-semibold text-slate-800 hover:underline">
                          {lead.company_name || demo.company_name || 'Unknown Company'}
                        </p>
                        <p className="text-sm text-slate-500">{lead.contact_name || demo.contact_name || 'Unknown Contact'}</p>
                      </div>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleQualifyDemo(demo.id)}>
                        <Shield className="w-4 h-4 mr-1" />
                        Mark as SQD
                      </Button>
                    </div>
                    
                    {/* Demo Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="bg-slate-50 p-2 rounded">
                        <p className="text-xs text-slate-500">Scheduled</p>
                        <p className="font-medium">{demoDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                        <p className="text-xs text-slate-600">{demoDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      
                      {completedDate && (
                        <div className="bg-slate-50 p-2 rounded">
                          <p className="text-xs text-slate-500">Completed</p>
                          <p className="font-medium">{completedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                          <p className="text-xs text-slate-600">{completedDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      )}
                      
                      {demo.demo_rating && (
                        <div className="bg-slate-50 p-2 rounded">
                          <p className="text-xs text-slate-500">Rating</p>
                          <p className="font-medium text-lg">{demo.demo_rating}<span className="text-sm text-slate-400">/10</span></p>
                        </div>
                      )}
                      
                      {demo.demo_done_by && (
                        <div className="bg-slate-50 p-2 rounded">
                          <p className="text-xs text-slate-500">Demo By</p>
                          <p className="font-medium text-xs">{demo.demo_done_by}</p>
                        </div>
                      )}
                      
                      {demo.duration_minutes && (
                        <div className="bg-slate-50 p-2 rounded">
                          <p className="text-xs text-slate-500">Duration</p>
                          <p className="font-medium">{demo.duration_minutes} mins</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Demo Notes */}
                    {demo.demo_notes && (
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="text-xs text-blue-600 font-medium">Notes:</p>
                        <p className="text-sm text-slate-700">{demo.demo_notes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="xl:col-span-2 bg-white border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Calendar</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <span className="font-medium text-slate-700 min-w-[110px] sm:min-w-[150px] text-center text-sm sm:text-base">{monthYear}</span>
                <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-1.5 sm:p-2 text-center text-[10px] sm:text-xs font-medium text-slate-500">
                  {day}
                </div>
              ))}
              {daysInMonth.map((day, index) => {
                const dayDemos = day ? getDemosForDate(day) : [];
                const isToday = day && day.toDateString() === today.toDateString();
                
                return (
                  <div
                    key={index}
                    className={cn(
                      'min-h-[56px] sm:min-h-[100px] p-1 sm:p-1.5 border rounded-lg',
                      day ? 'bg-white hover:bg-slate-50' : 'bg-slate-50',
                      isToday && 'ring-2 ring-blue-500'
                    )}
                  >
                    {day && (
                      <>
                        <span
                          className={cn(
                            'text-[10px] sm:text-xs font-medium',
                            isToday ? 'text-blue-600' : 'text-slate-600'
                          )}
                        >
                          {day.getDate()}
                        </span>
                        <div className="mt-1 space-y-1">
                          {dayDemos.slice(0, 1).map(demo => {
                            const lead = getLeadInfo(demo.lead_id);
                            return (
                              <div
                                key={demo.id}
                                className={cn(
                                  'text-[10px] sm:text-xs p-1 rounded truncate cursor-pointer flex items-center gap-1',
                                  demo.status === 'RESCHEDULED' ? 'bg-amber-100 text-amber-700' : 
                                  demo.is_sqd ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700',
                                  new Date(demo.demo_datetime) < today && !demo.is_sqd && 'grayscale opacity-70'
                                )}
                                title={lead.company_name}
                                onClick={() => navigate(`/leads/${demo.lead_id}`)}
                              >
                                {demo.is_sqd && <Flame className="w-3 h-3 fill-orange-500 text-orange-500" />}
                                {new Date(demo.demo_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                <span className="hidden sm:inline"> - {lead.company_name?.slice(0, 10)}</span>
                              </div>
                            );
                          })}
                          {dayDemos.length > 1 && (
                            <span className="text-[10px] sm:text-xs text-slate-500">+{dayDemos.length - 1} more</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Demos List */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Demos List</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {activeDemos.length > 0 ? (
              <div className="space-y-3">
                {activeDemos
                  .sort((a, b) => new Date(a.demo_datetime) - new Date(b.demo_datetime))
                  .map(demo => {
                    const lead = getLeadInfo(demo.lead_id);
                    const demoDate = new Date(demo.demo_datetime);
                    // Check if demo is in the past (before today's start)
                    const isPast = demoDate < today;
                    
                    return (
                      <div key={demo.id} className={cn(
                        'p-3 border rounded-lg',
                        isPast ? 'bg-slate-50 border-slate-200 grayscale opacity-75' : 'bg-white',
                        demo.is_sqd && 'border-emerald-200 bg-emerald-50 grayscale-0 opacity-100'
                      )}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-800 truncate">{lead.company_name || 'Unknown'}</p>
                                {demo.is_sqd && <Flame className="w-4 h-4 text-orange-500 fill-orange-500 animate-pulse" />}
                            </div>
                            <p className="text-xs text-slate-500">{lead.contact_name}</p>
                          </div>
                          <Badge className={cn('text-xs shrink-0', getStatusColor(demo.status))}>
                            {demo.status === 'RESCHEDULED' ? 'Resch.' : 
                             demo.status === 'COMPLETED_NOT_QUALIFIED' ? 'Awaiting' :
                             demo.status === 'COMPLETED_QUALIFIED_SQD' ? 'SQD' :
                             demo.is_sqd ? 'SQD' : 'Sched.'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{demoDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          <Clock className="w-4 h-4 ml-2" />
                          <span>{demoDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          {!isPast && !demo.is_sqd && (
                              <>
                                <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="flex-1 sm:flex-none"
                                    onClick={() => {
                                    setSelectedDemo(demo);
                                    // Use local datetime for form default
                                    const demoTime = new Date(demo.demo_datetime);
                                    const endTime = new Date(demoTime.getTime() + (demo.duration_minutes || 60) * 60000);
                                    const formatDateTime = (d) => {
                                        // Format as YYYY-MM-DDTHH:mm local
                                        const year = d.getFullYear();
                                        const month = String(d.getMonth() + 1).padStart(2, '0');
                                        const day = String(d.getDate()).padStart(2, '0');
                                        const hours = String(d.getHours()).padStart(2, '0');
                                        const mins = String(d.getMinutes()).padStart(2, '0');
                                        return `${year}-${month}-${day}T${hours}:${mins}`;
                                    };
                                    setCompleteDemoForm(prev => ({
                                        ...prev,
                                        demo_actual_start: formatDateTime(demoTime),
                                        demo_actual_end: formatDateTime(endTime)
                                    }));
                                    setIsCompleteDemoOpen(true);
                                    }}
                                >
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Done
                                </Button>
                                <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="flex-1 sm:flex-none"
                                    onClick={() => {
                                    setSelectedDemo(demo);
                                    setIsRescheduleDemoOpen(true);
                                    }}
                                >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Reschedule
                                </Button>
                                <Button
                                    size="sm" 
                                    variant="outline"
                                    className="flex-1 sm:flex-none text-red-600 hover:bg-red-50"
                                    onClick={() => handleCancelDemo(demo.id)}
                                >
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                </Button>
                              </>
                          )}
                          {isPast && !demo.is_sqd && (
                              <p className="text-xs text-slate-400 italic">Past event</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No demos found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Complete Demo Modal */}
        <Dialog open={isCompleteDemoOpen} onOpenChange={setIsCompleteDemoOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Mark Demo as Complete</DialogTitle>
              <DialogDescription>
                Enter the demo completion details. The lead will move to &quot;Qualification Pending&quot; status.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Demo Conducted By * (SaaS Company Persons)</Label>
                {completeDemoForm.demo_done_by.map((person, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={person}
                      onChange={(e) => updateDemoPerson(index, e.target.value)}
                      placeholder={`Person ${index + 1} name`}
                    />
                    {completeDemoForm.demo_done_by.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeDemoPerson(index)}>
                        <XCircle className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addDemoPerson}>
                  <Plus className="w-4 h-4 mr-1" /> Add Person
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label>Demo Rating * (out of 10)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={completeDemoForm.demo_rating}
                    onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_rating: Math.min(10, Math.max(1, parseInt(e.target.value) || 1))})}
                    className="w-20"
                  />
                  {/* Rating Stars - Removed to simplify for now or implement properly later if needed */}
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Demo Start Time *</Label>
                  <Input
                    type="datetime-local"
                    value={completeDemoForm.demo_actual_start}
                    onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_actual_start: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Demo End Time *</Label>
                  <Input
                    type="datetime-local"
                    value={completeDemoForm.demo_actual_end}
                    onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_actual_end: e.target.value})}
                  />
                </div>
              </div>
              
              {completeDemoForm.demo_actual_start && completeDemoForm.demo_actual_end && (
                <div className="p-3 bg-slate-100 rounded-lg flex items-center gap-2">
                  <Timer className="w-5 h-5 text-slate-600" />
                  <span className="font-medium">Total Duration: {calculateDemoDuration()} minutes</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Demo Notes</Label>
                <Textarea
                  value={completeDemoForm.demo_notes}
                  onChange={(e) => setCompleteDemoForm({...completeDemoForm, demo_notes: e.target.value})}
                  placeholder="Key takeaways, client feedback, next steps..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => { setIsCompleteDemoOpen(false); setSelectedDemo(null); }} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                onClick={handleCompleteDemo}
                disabled={
                  completeDemoForm.demo_done_by.filter(n => n.trim()).length === 0 ||
                  !completeDemoForm.demo_actual_start ||
                  !completeDemoForm.demo_actual_end
                }
                className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
              >
                Mark Complete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reschedule Demo Modal */}
        <Dialog open={isRescheduleDemoOpen} onOpenChange={setIsRescheduleDemoOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Reschedule Demo</DialogTitle>
              <DialogDescription>
                {selectedDemo && `Original: ${new Date(selectedDemo.demo_datetime).toLocaleString('en-IN')}`}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>New Date *</Label>
                  <Input
                    type="date"
                    value={rescheduleForm.demo_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, demo_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Time *</Label>
                  <Input
                    type="time"
                    value={rescheduleForm.demo_time}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, demo_time: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reason for Rescheduling</Label>
                <Textarea
                  value={rescheduleForm.reschedule_reason}
                  onChange={(e) => setRescheduleForm({...rescheduleForm, reschedule_reason: e.target.value})}
                  placeholder="Why is the demo being rescheduled?"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => { setIsRescheduleDemoOpen(false); setSelectedDemo(null); }} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                onClick={handleRescheduleDemo}
                disabled={!rescheduleForm.demo_date || !rescheduleForm.demo_time}
                className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto"
              >
                Reschedule Demo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DemosPage;
